import { Button, Spinner } from 'components/common';
import { AddToCart, CartLineQuantityInput } from 'lib/cart';
import { calculateNutritionalValues } from 'lib/nutrition';
import {
  isCustomizableMeal,
  isEmptyNutritionalValue,
  isMerch,
  Product,
} from 'lib/product';
import { useProductVariantAddonOptions } from 'lib/productAddon';
import ProductNutrient from 'lib/sellingProduct/ProductNutrient';
import ProductPrice from 'lib/sellingProduct/ProductPrice';
import useProductCustomization from 'lib/sellingProduct/useProductCustomization';
import { useMemo } from 'react';
import { isNil } from 'lib/javascript';
import AdditionalNutritionValues from './AdditionalNutritionValues';

const ProductActions = ({
  product,
  onAddToCart,
  onFirstItemAdded,
}: {
  product: Product;
  onAddToCart?: () => void;
  onFirstItemAdded?: () => void;
}) => {
  const {
    selectedVariant,
    selectedAddonOptionValueIds,
    quantity,
    setQuantity,
  } = useProductCustomization();

  const showNutritionValue = useMemo(() => {
    const isCustomizableProduct = isCustomizableMeal(product);

    if (isCustomizableProduct) return true;

    const isMerchProduct = isMerch(product);
    const isEmptyNutrition = isEmptyNutritionalValue(
      selectedVariant?.nutrition,
    );

    return !isMerchProduct && !isEmptyNutrition;
  }, [product, selectedVariant]);

  const {
    isLoading,
    selectAddonOptionValueById,
    selectIngredientById,
  } = useProductVariantAddonOptions(selectedVariant?.id);

  const selectedAddonOptionValues = useMemo(
    () =>
      selectedAddonOptionValueIds.map((selectedAddonOptionValueId) =>
        selectAddonOptionValueById(selectedAddonOptionValueId),
      ),
    [selectAddonOptionValueById, selectedAddonOptionValueIds],
  );

  const nutritionalValue = useMemo(
    () =>
      calculateNutritionalValues(
        selectedVariant,
        selectedAddonOptionValues,
        selectIngredientById,
      ),
    [
      selectIngredientById,
      selectedAddonOptionValues,
      selectedVariant,
    ],
  );

  const { calorie, fat, carb, protein, sodium } = nutritionalValue;

  const displayedNutritionalValues = () => {
    const constantValues = [];
    const productVariantNutritionalValues =
      selectedVariant?.nutrition?.constant ?? [];

    Object.keys(productVariantNutritionalValues).forEach((key) => {
      const value = productVariantNutritionalValues[key];
      if (!constantValues[key]) {
        constantValues[key] = [];
      }

      constantValues[key].push(value);
    });

    const ingredientsProportionalValues = selectedAddonOptionValues.reduce(
      (result, addonOption) => {
        const ingredientId = addonOption?.ingredient;
        const ingredient = selectIngredientById(ingredientId);

        if (ingredient && ingredient?.nutrition) {
          const { proportional, constant } = ingredient.nutrition;

          Object.keys(proportional).forEach((key) => {
            const value = proportional[key] ?? constant[key];

            if (!result[key]) {
              result[key] = [];
            }

            result[key].push(value);
          });
        }
        return result;
      },
      {},
    );

    const values = selectedAddonOptionValues?.length
      ? ingredientsProportionalValues
      : constantValues;

    const _displayedNutritionalValues = {};

    Object.keys(values).forEach((key) => {
      const nutrition = values[key];

      if (!_displayedNutritionalValues[key]) {
        _displayedNutritionalValues[key] = [];
      }

      const shouldShow = nutrition.every((value) => !isNil(value));

      _displayedNutritionalValues[key] = shouldShow;
    });

    return _displayedNutritionalValues;
  };

  if (isLoading) return null;

  return (
    <div className="tw-flex tw-items-start md:tw-items-center tw-justify-between tw-w-full tw-flex-col md:tw-flex-row tw-relative">
      {showNutritionValue && (
        <>
          <AdditionalNutritionValues
            nutritionalValue={nutritionalValue}
            displayedNutritionalValues={displayedNutritionalValues()}
          />
          <div className="tw-flex tw-gap-4 md:tw-gap-6 tw-flex-1 tw-w-full tw-items-end tw-justify-between md:tw-justify-start tw-px-4 lg:tw-px-8 tw-py-6">
            <ProductNutrient
              name="Calories"
              value={calorie ?? 0}
              fontSize="lg"
            />
            <ProductNutrient
              name="Fats"
              value={fat ?? 0}
              unit="g"
              fontSize="lg"
            />
            <ProductNutrient
              name="Carbs"
              value={carb ?? 0}
              unit="g"
              fontSize="lg"
            />
            <ProductNutrient
              name="Protein"
              value={protein ?? 0}
              unit="g"
              fontSize="lg"
            />
            <ProductNutrient
              name="Sodium"
              value={sodium ?? 0}
              unit="mg"
              fontSize="lg"
            />
          </div>
        </>
      )}
      <div className="tw-flex tw-gap-4 tw-items-center tw-justify-end tw-p-0 md:tw-px-8 md:tw-py-6 tw-w-full">
        <ProductPrice
          // @ts-ignore
          variant={selectedVariant}
          addonOptionValues={selectedAddonOptionValues}
          className="tw-mb-0 tw-hidden lg:tw-inline-flex"
        />

        <div className="tw-max-w-[120px] tw-hidden lg:tw-block">
          <CartLineQuantityInput
            id="qty"
            name="quantity"
            // @ts-ignore\
            productVariant={selectedVariant}
            value={quantity}
            onChange={setQuantity}
            data-decimals="0"
            required
          />
        </div>

        <AddToCart
          // @ts-ignore
          product={product}
          // @ts-ignore
          productVariant={selectedVariant}
          quantity={quantity}
          // @ts-ignore
          addonOptionValueIds={selectedAddonOptionValueIds}
          onFirstItemAdded={onFirstItemAdded}
        >
          {({
            loading,
            isOutOfStock,
            canAddToCart,
            addToCart,
            isAddingToCart,
          }) => {
            const getLabel = () => {
              if (loading) return 'Loading...';
              if (isOutOfStock) return 'We Sold Out!';
              return 'ADD TO CART';
            };

            const disabled =
              !canAddToCart ||
              loading ||
              isOutOfStock ||
              isAddingToCart;

            const handleAddToCart = async () => {
              try {
                await addToCart();
                onAddToCart?.();
              } catch (err) {}
            };

            return (
              <>
                {/* @ts-ignore */}
                <Button
                  className="tw-w-full tw-min-w-0 lg:tw-w-auto tw-m-0 tw-block lg:tw-hidden"
                  disabled={disabled}
                  color={disabled ? 'dark' : 'primary'}
                  // @ts-ignore
                  onClick={handleAddToCart}
                >
                  {isAddingToCart ? (
                    <span className="tw-mr-1 tw-inline-flex">
                      <Spinner size="sm" />
                    </span>
                  ) : (
                    <i className="icon-cart-plus tw-ml-0 tw-mr-1.5" />
                  )}
                  {getLabel()}
                </Button>
                {/* @ts-ignore */}
                <Button
                  className="tw-w-full tw-min-w-0 lg:tw-w-auto tw-m-0 tw-hidden lg:tw-block"
                  disabled={disabled}
                  color={disabled ? 'dark' : 'primary'}
                  variant="outlined"
                  // @ts-ignore
                  onClick={handleAddToCart}
                >
                  {isAddingToCart ? (
                    <span className="tw-mr-1 tw-inline-flex">
                      <Spinner size="sm" />
                    </span>
                  ) : (
                    <i className="icon-cart-plus tw-ml-0 tw-mr-1.5" />
                  )}
                  {getLabel()}
                </Button>
              </>
            );
          }}
        </AddToCart>
      </div>
    </div>
  );
};

export default ProductActions;
